import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Plus, Trash2, Pencil, Check, X, Power } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL;

const FAMILY_LABELS = {
  sales: 'Sales & Growth',
  front_of_house: 'Front-of-House / Service',
  technical_craft: 'Technical / Craft',
  management: 'Management',
};

const SKILL_AREA_LABELS = {
  numerical: 'Numerical',
  verbal: 'Verbal',
  logical: 'Logical',
  attention_to_detail: 'Attention to Detail',
};

const SUB_TABS = [
  { key: 'aptitude', label: 'Aptitude Questions' },
  { key: 'sjt', label: 'Scenario Questions' },
  { key: 'video', label: 'Video Questions' },
];

const emptyOption = (key) => ({ key, text: '', points: 0 });

function OptionEditor({ options, onChange }) {
  const update = (idx, field, value) => {
    const next = [...options];
    next[idx] = { ...next[idx], [field]: field === 'points' ? Number(value) : value };
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {options.map((opt, idx) => (
        <div key={opt.key} className="flex items-center gap-2">
          <span className="w-5 text-xs font-semibold text-gray-400">{opt.key}</span>
          <input
            value={opt.text}
            onChange={(e) => update(idx, 'text', e.target.value)}
            placeholder="Option text"
            className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-sm"
          />
          <input
            type="number" min={0} max={100} value={opt.points}
            onChange={(e) => update(idx, 'points', e.target.value)}
            className="w-20 px-2 py-1.5 border border-gray-200 rounded text-sm"
            title="Points (0-100)"
          />
        </div>
      ))}
    </div>
  );
}

function QuestionCard({ item, rubricCompetencies, onSave, onDelete, onToggleActive }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item);

  useEffect(() => { setDraft(item); }, [item]);

  const save = async () => {
    await onSave(item._id, draft);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className={`border rounded-lg p-3 ${item.active ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm text-gray-800 flex-1">{item.prompt}</p>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => setEditing(true)} className="p-1 text-gray-400 hover:text-emerald-600" title="Edit"><Pencil size={14} /></button>
            <button onClick={() => onToggleActive(item)} className="p-1 text-gray-400 hover:text-amber-600" title={item.active ? 'Deactivate' : 'Activate'}><Power size={14} /></button>
            <button onClick={() => onDelete(item._id)} className="p-1 text-gray-400 hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
          </div>
        </div>
        <ul className="mt-2 space-y-0.5">
          {item.options.map((o) => (
            <li key={o.key} className="text-xs text-gray-500">
              <span className="font-medium">{o.key}.</span> {o.text} <span className="text-gray-400">({o.points}pts)</span>
            </li>
          ))}
        </ul>
        {item.assessedCompetency && (
          <p className="mt-1 text-[11px] text-purple-500">Tests: {item.assessedCompetency.replace(/_/g, ' ')}</p>
        )}
        {!item.active && <p className="mt-1 text-[11px] text-gray-400 italic">Inactive, not served to candidates</p>}
      </div>
    );
  }

  return (
    <div className="border border-emerald-200 rounded-lg p-3 bg-emerald-50/30">
      <textarea
        value={draft.prompt}
        onChange={(e) => setDraft({ ...draft, prompt: e.target.value })}
        rows={2}
        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm mb-2"
      />
      <OptionEditor options={draft.options} onChange={(options) => setDraft({ ...draft, options })} />
      {item.itemType === 'sjt' && rubricCompetencies && (
        <select
          value={draft.assessedCompetency || ''}
          onChange={(e) => setDraft({ ...draft, assessedCompetency: e.target.value })}
          className="mt-2 w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
        >
          <option value="">Which competency does this test?</option>
          {rubricCompetencies.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      )}
      <div className="flex gap-2 mt-3">
        <button onClick={save} className="flex items-center gap-1 text-xs bg-emerald-600 text-white px-3 py-1.5 rounded-lg"><Check size={13} /> Save</button>
        <button onClick={() => { setDraft(item); setEditing(false); }} className="flex items-center gap-1 text-xs bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg"><X size={13} /> Cancel</button>
      </div>
    </div>
  );
}

function AddQuestionForm({ itemType, extraFields, rubricCompetencies, onAdd }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [options, setOptions] = useState(['A', 'B', 'C', 'D'].map(emptyOption));
  const [extra, setExtra] = useState({});

  const submit = async () => {
    if (!prompt.trim()) { toast.error('Enter a question prompt'); return; }
    await onAdd({ itemType, prompt, options, ...extraFields, ...extra });
    setPrompt('');
    setOptions(['A', 'B', 'C', 'D'].map(emptyOption));
    setExtra({});
    setOpen(false);
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-800">
        <Plus size={16} /> Add Question
      </button>
    );
  }

  return (
    <div className="border border-emerald-200 rounded-lg p-3 bg-emerald-50/30">
      <textarea
        value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
        placeholder="Question prompt" className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm mb-2"
      />
      <OptionEditor options={options} onChange={setOptions} />
      {itemType === 'sjt' && rubricCompetencies && (
        <select
          value={extra.assessedCompetency || ''}
          onChange={(e) => setExtra({ ...extra, assessedCompetency: e.target.value })}
          className="mt-2 w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
        >
          <option value="">Which competency does this test?</option>
          {rubricCompetencies.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      )}
      <div className="flex gap-2 mt-3">
        <button onClick={submit} className="flex items-center gap-1 text-xs bg-emerald-600 text-white px-3 py-1.5 rounded-lg"><Check size={13} /> Add</button>
        <button onClick={() => setOpen(false)} className="flex items-center gap-1 text-xs bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg"><X size={13} /> Cancel</button>
      </div>
    </div>
  );
}

function VideoPromptRow({ item, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.prompt);

  const save = async () => {
    await onSave(item._id, { prompt: text });
    setEditing(false);
  };

  return (
    <div className="flex items-start gap-2 border border-gray-200 rounded-lg p-3 bg-white">
      {editing ? (
        <>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm" />
          <button onClick={save} className="p-1.5 text-emerald-600" title="Save"><Check size={16} /></button>
          <button onClick={() => { setText(item.prompt); setEditing(false); }} className="p-1.5 text-gray-400" title="Cancel"><X size={16} /></button>
        </>
      ) : (
        <>
          <p className="flex-1 text-sm text-gray-800">{item.prompt}</p>
          <button onClick={() => setEditing(true)} className="p-1.5 text-gray-400 hover:text-emerald-600" title="Edit"><Pencil size={14} /></button>
          <button onClick={() => onDelete(item._id)} className="p-1.5 text-gray-400 hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
        </>
      )}
    </div>
  );
}

function ItemBank({ rubrics }) {
  const [subTab, setSubTab] = useState('aptitude');
  const [items, setItems] = useState([]);
  const [videoPrompts, setVideoPrompts] = useState([]);
  const [activeFamily, setActiveFamily] = useState('sales');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (subTab === 'video') {
        const res = await axios.get(`${API_URL}/api/recruitment/video-prompts`);
        setVideoPrompts(res.data.prompts);
      } else {
        const res = await axios.get(`${API_URL}/api/recruitment/assessment-items`, { params: { itemType: subTab } });
        setItems(res.data.items);
      }
    } catch {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, [subTab]);

  useEffect(() => { load(); }, [load]);

  const saveItem = async (id, data) => {
    try {
      await axios.put(`${API_URL}/api/recruitment/assessment-items/${id}`, data);
      toast.success('Saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this question permanently?')) return;
    try {
      await axios.delete(`${API_URL}/api/recruitment/assessment-items/${id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const toggleActive = async (item) => {
    await saveItem(item._id, { active: !item.active });
  };

  const addItem = async (data) => {
    try {
      await axios.post(`${API_URL}/api/recruitment/assessment-items`, data);
      toast.success('Question added');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add');
    }
  };

  const saveVideoPrompt = async (id, data) => {
    try {
      await axios.put(`${API_URL}/api/recruitment/video-prompts/${id}`, data);
      toast.success('Saved');
      load();
    } catch {
      toast.error('Failed to save');
    }
  };

  const deleteVideoPrompt = async (id) => {
    if (!window.confirm('Delete this video question permanently?')) return;
    try {
      await axios.delete(`${API_URL}/api/recruitment/video-prompts/${id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const addVideoPrompt = async (roleFamily) => {
    const prompt = window.prompt('New video question:');
    if (!prompt) return;
    try {
      await axios.post(`${API_URL}/api/recruitment/video-prompts`, { roleFamily, prompt, order: 99 });
      toast.success('Added');
      load();
    } catch {
      toast.error('Failed to add');
    }
  };

  const rubricFor = (family) => rubrics.find((r) => r.roleFamily === family)?.competencies;

  return (
    <div>
      <div className="flex gap-2 mb-6 flex-wrap">
        {SUB_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${
              subTab === t.key ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'aptitude' && (
        <div>
          <p className="text-xs text-gray-500 mb-4">
            Every candidate gets a random mix of these, weighted by their role. Deactivate a question
            to stop using it without losing past candidates&rsquo; scores.
          </p>
          {loading ? <p className="text-sm text-gray-400">Loading...</p> : (
            Object.keys(SKILL_AREA_LABELS).map((area) => (
              <div key={area} className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">{SKILL_AREA_LABELS[area]}</h4>
                <div className="space-y-2">
                  {items.filter((i) => i.skillArea === area).map((item) => (
                    <QuestionCard key={item._id} item={item} onSave={saveItem} onDelete={deleteItem} onToggleActive={toggleActive} />
                  ))}
                </div>
                <div className="mt-2">
                  <AddQuestionForm itemType="aptitude" extraFields={{ skillArea: area }} onAdd={addItem} />
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {subTab === 'sjt' && (
        <div>
          <p className="text-xs text-gray-500 mb-4">
            Scenario questions are written per role family and each one is tagged with the competency
            it tests, so the CEO&rsquo;s report can explain the &ldquo;why&rdquo; behind a candidate&rsquo;s score.
          </p>
          <div className="flex gap-2 mb-4 flex-wrap">
            {Object.entries(FAMILY_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveFamily(key)}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  activeFamily === key ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {loading ? <p className="text-sm text-gray-400">Loading...</p> : (
            <div className="space-y-2">
              {items.filter((i) => i.roleFamily === activeFamily).map((item) => (
                <QuestionCard
                  key={item._id} item={item} rubricCompetencies={rubricFor(activeFamily)}
                  onSave={saveItem} onDelete={deleteItem} onToggleActive={toggleActive}
                />
              ))}
            </div>
          )}
          <div className="mt-3">
            <AddQuestionForm itemType="sjt" extraFields={{ roleFamily: activeFamily }} rubricCompetencies={rubricFor(activeFamily)} onAdd={addItem} />
          </div>
        </div>
      )}

      {subTab === 'video' && (
        <div>
          <p className="text-xs text-gray-500 mb-4">
            Candidates record a short video answer to each of these, one per role family. AI scores
            the answers, you confirm.
          </p>
          <div className="flex gap-2 mb-4 flex-wrap">
            {Object.entries(FAMILY_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveFamily(key)}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  activeFamily === key ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {loading ? <p className="text-sm text-gray-400">Loading...</p> : (
            <div className="space-y-2">
              {videoPrompts.filter((p) => p.roleFamily === activeFamily).map((item) => (
                <VideoPromptRow key={item._id} item={item} onSave={saveVideoPrompt} onDelete={deleteVideoPrompt} />
              ))}
            </div>
          )}
          <button onClick={() => addVideoPrompt(activeFamily)} className="mt-3 flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-800">
            <Plus size={16} /> Add Video Question
          </button>
        </div>
      )}
    </div>
  );
}

export default ItemBank;
